import { useRouter } from "next/router";
import { useCallback } from "react";
import {
  CUSTOMER_ANALYTICS_EVENT,
  PagesEnum,
  TabsEnum,
} from "../../../utils/enums";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import { LoyaltyOfferCategory } from "../../../utils/loyaltyTypes";
import { useCustomerAnalytics } from "../useCustomerAnalytics";
import { useAppSelector } from "../useReduxHooks";

/** Where a customer applied a reward from, as the free-delivery events name it. */
export enum OfferApplySurface {
  Menu = "MENU",
  Cart = "CART",
  RewardsTab = "REWARDS_TAB",
  Popup = "POPUP",
}

const FREE_DELIVERY_EVENT_BY_SURFACE: Record<
  OfferApplySurface,
  CUSTOMER_ANALYTICS_EVENT
> = {
  [OfferApplySurface.Menu]:
    CUSTOMER_ANALYTICS_EVENT.APPLIED_FREE_DELIVERY_FROM_MENU,
  [OfferApplySurface.Cart]:
    CUSTOMER_ANALYTICS_EVENT.APPLIED_FREE_DELIVERY_FROM_CART,
  [OfferApplySurface.RewardsTab]:
    CUSTOMER_ANALYTICS_EVENT.APPLIED_FREE_DELIVERY_FROM_REWARDS_TAB,
  [OfferApplySurface.Popup]:
    CUSTOMER_ANALYTICS_EVENT.APPLIED_FREE_DELIVERY_FROM_POPUP,
};

/**
 * The surface the customer is looking at.
 *
 * The rewards tab and the all-rewards page are one surface: both are the
 * customer's reward list, and the story counts them together. `Popup` is not
 * derivable from the route — a server-pushed pop-up sits on top of whatever
 * page the customer was already on — so callers that know they are one pass it
 * in explicitly.
 */
const useOfferApplySurface = (): OfferApplySurface => {
  const router = useRouter();
  // Optional: the surface is a hint for analytics, so a store without this
  // slice yet should fall through to the route rather than crash the component
  // that asked.
  const currentTab = useAppSelector(
    (state) => state.globalComponentsToggleSlice?.currentHomeScreenTab
  );

  if (router.pathname.includes(PagesEnum.Cart)) {
    return OfferApplySurface.Cart;
  }

  if (
    router.pathname.includes(PagesEnum.AllRewards) ||
    currentTab === TabsEnum.Loyalty
  ) {
    return OfferApplySurface.RewardsTab;
  }

  return OfferApplySurface.Menu;
};

/**
 * Report a free-delivery redemption, per surface.
 *
 * Free delivery gets its own events rather than riding the generic
 * `APPLY_REWARD_*` ones so redemptions can be counted against delivery orders —
 * which is also why every surface reports, including the two the generic events
 * have always missed (the menu and the all-rewards page). Any other offer
 * category is left to those generic events and reports nothing here.
 */
export const useTrackFreeDeliveryApply = () => {
  const addCustomerAnalytics = useCustomerAnalytics();
  const detectedSurface = useOfferApplySurface();

  return useCallback(
    (offer: LoyaltyOffer, surface: OfferApplySurface = detectedSurface) => {
      if (offer.offerCategory !== LoyaltyOfferCategory.DELIVERY_DISCOUNT) {
        return;
      }

      addCustomerAnalytics(FREE_DELIVERY_EVENT_BY_SURFACE[surface], {
        offerId: offer.id,
        offerType: offer.valueType,
        offerSystem: offer.offerSystem,
      });
    },
    [addCustomerAnalytics, detectedSurface]
  );
};
